// Rudras-AI printing dashboard — pure model helpers (Phase 5).
//
// Everything here is framework-free so it can be unit-tested directly:
// labels operators understand, badge specs that never rely on colour
// alone, the client mirror of the backend reprint policy, printer-form
// validation that mirrors backend/printing/profiles.py, inheritance
// helpers for tenant defaults vs catalog overrides, operator-facing
// error messages, and overview aggregation.

export const DOC_TYPES = ["customer_receipt", "kitchen_ticket", "packing_slip"];

// ------------------------------------------- automatic packing slips (Oct 2026)
// The two packing-slip rules owned by Automatic Packing Slips carry this
// stable metadata (backend `printing/packing_slip_rules.py`). They are
// changed only from the Overview; packing-slip rules are never created
// from New Rule.
export const MANAGED_BY_AUTO_PACKING_SLIPS = "automatic_packing_slips";
export const MANAGED_RULE_LABEL = "Managed by Automatic Packing Slips";
export const LEGACY_PACKING_SLIP_RULE_LABEL = "Legacy/advanced rule — automatic packing slips are managed from Overview.";
export const MANAGED_PAIR_MESSAGE = "Automatic packing slips are active for cash and card payments. Only the rule matching the order's payment method fires.";
export const NEEDS_SETUP_MESSAGE = "Automatic packing slips are active, but the rules need to be secured as managed rules.";
export const AWAITING_SETUP_LABEL = "Automatic Packing Slips rule — awaiting Finish setup on the Overview.";
export const NEW_RULE_DOC_TYPES = DOC_TYPES.filter((d) => d !== "packing_slip");

export function isManagedRule(rule) {
  return Boolean(rule && rule.managed_by === MANAGED_BY_AUTO_PACKING_SLIPS);
}

/** Ids of the earlier version's rules that Finish setup would secure. */
export function awaitingSetupRuleIds(summary) {
  const fs = summary?.finish_setup;
  if (!fs?.needed) return [];
  return Object.values(fs.rules || {}).flat().map((r) => r.id);
}

/**
 * Notices for the Rules & Triggers tab: one per document type with more
 * than one enabled effective rule, except that the valid managed pair
 * (exactly the cash + card rules of Automatic Packing Slips, nothing else
 * enabled for packing slips — server summary included) is an
 * informational notice instead of a duplicate warning. Any other
 * packing-slip configuration that could print more than one slip per
 * order keeps a prominent warning.
 */
export function ruleNotices(effective, summary) {
  const notices = [];
  const enabled = (effective || []).filter((r) => r.enabled);
  const counts = {};
  enabled.forEach((r) => { counts[r.doc_type] = (counts[r.doc_type] || 0) + 1; });
  const slips = enabled.filter((r) => r.doc_type === "packing_slip");
  const conflicts = summary?.conflicts || {};
  const otherEnabled = [
    ...(conflicts.legacy_enabled || []),
    ...(conflicts.duplicate_managed || []),
    ...((summary?.catalog_rules || []).filter((r) => r.enabled)),
  ];
  const roles = slips.filter(isManagedRule).map((r) => r.managed_role).sort().join(",");
  const awaiting = awaitingSetupRuleIds(summary);
  if (awaiting.length && otherEnabled.length === 0 && slips.every((r) => awaiting.includes(r.id))) {
    // The earlier version's own cash + card pair: not a duplicate, but not
    // yet secured either (Product Owner, Oct 2026).
    Object.entries(counts).forEach(([dt, n]) => {
      if (dt !== "packing_slip" && n > 1) notices.push({ docType: dt, tone: "warning", testId: `rules-multi-${dt}`, message: `${DOC_TYPE_LABELS[dt] || dt}: ${n} enabled rules — each firing event will produce its own document.` });
    });
    notices.push({ docType: "packing_slip", tone: "warning", testId: "rules-packing-slips-needs-setup", message: `${NEEDS_SETUP_MESSAGE} Use Finish setup on the Overview.` });
    return notices;
  }
  const validPair = slips.length === 2 && slips.every(isManagedRule) && roles === "card,cash" && otherEnabled.length === 0;
  Object.entries(counts).forEach(([dt, n]) => {
    if (dt === "packing_slip") return;
    if (n > 1) notices.push({ docType: dt, tone: "warning", testId: `rules-multi-${dt}`, message: `${DOC_TYPE_LABELS[dt] || dt}: ${n} enabled rules — each firing event will produce its own document.` });
  });
  if (validPair) {
    notices.push({ docType: "packing_slip", tone: "info", testId: "rules-managed-packing-slips", message: MANAGED_PAIR_MESSAGE });
  } else if (slips.length > 1 || (slips.some(isManagedRule) && otherEnabled.length > 0)) {
    const extra = otherEnabled.length ? ` Other enabled packing-slip rules: ${otherEnabled.map((r) => r.name || r.id).join(", ")}.` : "";
    notices.push({
      docType: "packing_slip", tone: "danger", testId: "rules-multi-packing_slip",
      message: `Packing slip: duplicate risk — ${Math.max(slips.length, 2)} enabled packing-slip rules could print more than one packing slip for an order.${extra} Disable every packing-slip rule that is not managed by Automatic Packing Slips.`,
    });
  }
  return notices;
}

export const DOC_TYPE_LABELS = {
  customer_receipt: "Customer receipt",
  kitchen_ticket: "Kitchen ticket",
  packing_slip: "Packing slip",
  order_confirmation: "Order confirmation (legacy)",
};

export const REPRINT_LABELS = {
  customer_receipt: "DUPLICATE",
  packing_slip: "DUPLICATE",
  kitchen_ticket: "REPRINT",
  order_confirmation: "DUPLICATE",
};

export const TRIGGERS = [
  { id: "order_received", label: "Order received", note: "May print before payment. A pre-payment receipt must not say Paid." },
  { id: "payment_paid", label: "Payment paid / captured", note: "" },
  { id: "tenant_accepted", label: "Order accepted by the business", note: "" },
  { id: "cash_received", label: "Cash received", note: "" },
  { id: "kds_completed", label: "Kitchen completed (KDS)", note: "" },
  { id: "manual_print", label: "Manual print", note: "" },
  { id: "manual_reprint", label: "Manual reprint", note: "" },
];
export const TRIGGER_LABELS = Object.fromEntries(TRIGGERS.map((t) => [t.id, t.label]));

export const ROLE_LABELS = {
  receipt: "Customer receipt printer",
  kitchen: "Kitchen printer",
  packing: "Packing printer",
  general: "General (any document)",
};

export const SOURCE_LABELS = {
  system: "System default",
  tenant: "Tenant default",
  catalog: "Catalog override",
};

// Job states → operator badge. `glyph` is a text marker so status is
// never conveyed by colour alone; `tone` picks the colour family.
export const JOB_STATES = {
  queued: { label: "Queued", tone: "neutral", glyph: "○", terminal: false },
  claimed: { label: "Claimed by agent", tone: "info", glyph: "◔", terminal: false },
  rendering: { label: "In progress", tone: "info", glyph: "◑", terminal: false },
  submitted_to_spooler: { label: "Submitted to spooler", tone: "info", glyph: "◑", terminal: false },
  transmitting: { label: "Transmitting", tone: "info", glyph: "◑", terminal: false },
  transmitted: { label: "Transmitted", tone: "success", glyph: "✓", terminal: true },
  completed: { label: "Transmitted (legacy)", tone: "success", glyph: "✓", terminal: true },
  failed_retryable: { label: "Retry pending", tone: "warning", glyph: "↻", terminal: false },
  failed_terminal: { label: "Failed", tone: "danger", glyph: "✕", terminal: true },
  failed: { label: "Failed (legacy)", tone: "danger", glyph: "✕", terminal: true },
  ambiguous: { label: "Needs attention — outcome unknown", tone: "danger", glyph: "?", terminal: true },
  cancelled: { label: "Cancelled", tone: "neutral", glyph: "–", terminal: true },
};

export function jobStateSpec(state) {
  return JOB_STATES[state] || { label: `Unknown (${state || "?"})`, tone: "neutral", glyph: "?", terminal: false };
}

export const TRANSMITTED_EXPLANATION =
  "Transmitted means the job was delivered to the printer connection. It does not confirm that paper physically printed.";

// ----------------------------------------------------------- reprint policy
// Mirrors backend/printing/service.py reprint_policy_for so the list view
// can disable the button before the detail call; the backend remains the
// authority and its 409 responses are surfaced verbatim.
const REPRINT_ALLOWED = new Set(["transmitted", "completed", "failed_terminal"]);
const REPRINT_ACK = new Set(["ambiguous", "failed"]);
const REPRINT_BLOCKED = {
  queued: "The original job is still queued. Cancel it or let it print; a reprint would create a duplicate.",
  claimed: "The original job is being processed by the agent. Wait for a final outcome.",
  rendering: "The original job is being processed. Wait for a final outcome.",
  submitted_to_spooler: "The original job is being processed. Wait for a final outcome.",
  transmitting: "The original job is being transmitted right now. Wait for a final outcome.",
  failed_retryable: "The original job is scheduled for retry. Resolve or cancel it before reprinting.",
  cancelled: "Cancelled jobs cannot be reprinted in this phase.",
};

export function reprintPolicyFor(job) {
  if (job && job.reprint_policy) return job.reprint_policy;
  const state = job ? job.state : undefined;
  if (REPRINT_ALLOWED.has(state)) {
    return { allowed: true, requires_acknowledgement: false, code: "reprint_allowed", message: "A labelled reprint may be created.", original_state: state };
  }
  if (REPRINT_ACK.has(state)) {
    return {
      allowed: true, requires_acknowledgement: true, code: "duplicate_risk_unacknowledged",
      message: "The printer may already have produced this document. Confirm the duplicate risk before reprinting.",
      original_state: state,
    };
  }
  return {
    allowed: false, requires_acknowledgement: false,
    code: REPRINT_BLOCKED[state] ? `reprint_blocked_${state}` : "reprint_blocked_unknown_state",
    message: REPRINT_BLOCKED[state] || "This job is in an unrecognised state and cannot be reprinted safely.",
    original_state: state,
  };
}

export function reprintLabelFor(docType) {
  return REPRINT_LABELS[docType] || "DUPLICATE";
}

export function newRequestKey() {
  try {
    if (typeof crypto !== "undefined" && crypto.randomUUID) return `dash-${crypto.randomUUID()}`;
  } catch (_e) { /* fall through */ }
  return `dash-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 12)}`;
}

// ------------------------------------------------------- printer form rules
// Mirrors backend/printing/profiles.py destination policy: private IPv4
// (10/8, 172.16/12, 192.168/16) or a single-label / .local / .lan / .home /
// .internal hostname. Public addresses, loopback and IPv6 are rejected
// here first so the operator sees the reason immediately; the backend
// re-validates.
const IPV4 = /^(\d{1,3})\.(\d{1,3})\.(\d{1,3})\.(\d{1,3})$/;
const LOCAL_HOST = /^[a-z0-9](?:[a-z0-9-]{0,61}[a-z0-9])?(?:\.(?:local|lan|home|internal))?$/i;

export function hostProblem(raw) {
  const host = (raw || "").trim();
  if (!host) return "Enter the printer's LAN IP address or local hostname.";
  if (host.includes(":")) return "IPv6 addresses are not supported. Use the printer's IPv4 LAN address.";
  const m = IPV4.exec(host);
  if (m) {
    const o = m.slice(1).map(Number);
    if (o.some((n) => n > 255)) return "That is not a valid IPv4 address.";
    if (o[0] === 10) return null;
    if (o[0] === 172 && o[1] >= 16 && o[1] <= 31) return null;
    if (o[0] === 192 && o[1] === 168) return null;
    if (o[0] === 127) return "Loopback is not a printer address.";
    return "Only private LAN addresses are allowed (10.x, 172.16–31.x, 192.168.x). Public Internet destinations are not permitted.";
  }
  if (LOCAL_HOST.test(host)) return null;
  return "Use a private IPv4 address or a local hostname (for example printer-1 or kitchen.local).";
}

export const PRINTER_FORM_DEFAULTS = {
  name: "", location_id: "", role: "receipt", provider: "escpos_network", profile_id: "adesso_nuprint_330",
  host: "", port: "9100", connect_timeout_ms: "3000", write_timeout_ms: "5000", paper_width_mm: "80",
  columns: "48", code_page: "cp437", cut_profile: "gs_v_66_0", cash_drawer_enabled: false,
  agent_id: "", enabled: true, is_default: false,
};

function intIn(value, min, max) {
  if (value === "" || value === null || value === undefined) return null;
  const n = Number(value);
  if (!Number.isInteger(n) || n < min || n > max) return undefined;
  return n;
}

export function validatePrinterForm(form, { profiles = [], agents = [] } = {}) {
  const errors = {};
  const name = (form.name || "").trim();
  if (!name) errors.name = "Give the printer a name staff will recognise.";
  else if (/[<>]/.test(name)) errors.name = "Names cannot contain < or >.";
  const hp = hostProblem(form.host);
  if (hp) errors.host = hp;
  const port = intIn(form.port === "" ? "9100" : form.port, 1, 65535);
  if (port === undefined) errors.port = "Port must be a whole number between 1 and 65535 (raw ESC/POS printers use 9100).";
  const ct = intIn(form.connect_timeout_ms, 100, 60000);
  if (ct === undefined) errors.connect_timeout_ms = "Connect timeout must be 100–60000 ms.";
  const wt = intIn(form.write_timeout_ms, 100, 60000);
  if (wt === undefined) errors.write_timeout_ms = "Write timeout must be 100–60000 ms.";
  const paper = intIn(form.paper_width_mm, 40, 120);
  if (paper === undefined) errors.paper_width_mm = "Paper width must be 40–120 mm.";
  const columns = intIn(form.columns, 16, 96);
  if (columns === undefined) errors.columns = "Columns must be 16–96.";
  if (!form.profile_id) errors.profile_id = "Choose a certified printer profile.";
  else if (profiles.length && !profiles.some((p) => p.id === form.profile_id)) errors.profile_id = "That profile is not certified in this system.";
  if (form.provider && form.provider !== "escpos_network") errors.provider = "Only Network ESC/POS printers can be added here.";
  if (form.agent_id && agents.length && !agents.some((a) => a.id === form.agent_id && !a.revoked_at)) {
    errors.agent_id = "Choose an active print agent.";
  }
  if (!["receipt", "kitchen", "packing", "general"].includes(form.role)) errors.role = "Choose a printer role.";
  const payload = {
    name, profile_id: form.profile_id, host: (form.host || "").trim(), port: port ?? 9100,
    provider: "escpos_network", role: form.role, location_id: (form.location_id || "").trim() || null,
    agent_id: form.agent_id || null, connect_timeout_ms: ct, write_timeout_ms: wt, paper_width_mm: paper,
    columns, code_page: form.code_page || null, cut_profile: form.cut_profile || null,
    cash_drawer_enabled: Boolean(form.cash_drawer_enabled), status_query_enabled: false,
    is_default: Boolean(form.is_default), enabled: form.enabled !== false,
  };
  return { valid: Object.keys(errors).length === 0, errors, payload };
}

export function printerToForm(p) {
  return {
    ...PRINTER_FORM_DEFAULTS,
    name: p.name || "", location_id: p.location_id || "", role: p.role || "general",
    provider: p.provider || "escpos_network", profile_id: p.profile_id || "", host: p.host || "",
    port: String(p.port ?? 9100), connect_timeout_ms: String(p.connect_timeout_ms ?? 3000),
    write_timeout_ms: String(p.write_timeout_ms ?? 5000), paper_width_mm: String(p.paper_width_mm ?? 80),
    columns: String(p.columns ?? 48), code_page: p.code_page || "cp437", cut_profile: p.cut_profile || "gs_v_66_0",
    cash_drawer_enabled: Boolean(p.cash_drawer_enabled), agent_id: p.agent_id || "",
    enabled: p.enabled !== false, is_default: Boolean(p.is_default),
  };
}

/** PATCH body containing only the fields that differ from the stored printer. */
export function printerPatch(stored, payload) {
  const patch = {};
  const keys = ["name", "location_id", "role", "agent_id", "profile_id", "host", "port", "connect_timeout_ms",
    "write_timeout_ms", "paper_width_mm", "columns", "code_page", "cut_profile", "cash_drawer_enabled", "is_default", "enabled"];
  for (const k of keys) {
    const before = stored[k] === undefined ? null : stored[k];
    const after = payload[k] === undefined ? null : payload[k];
    if (before !== after) patch[k] = after;
  }
  return patch;
}

// --------------------------------------------------------- document settings
export const DOC_SETTING_FIELDS = ["enabled", "auto_print", "printer_role", "printer_id", "copy_count", "layout_id", "layout_version", "promo_footer", "trigger_events"];

export const DOC_SETTING_LABELS = {
  enabled: "Printing enabled",
  auto_print: "Print automatically",
  printer_role: "Printer role",
  printer_id: "Specific printer",
  copy_count: "Copies",
  layout_id: "Layout",
  layout_version: "Layout version",
  promo_footer: "Promotional footer",
  trigger_events: "Print when",
};

export function isCopyCountValid(n) {
  return Number.isInteger(n) && n >= 1 && n <= 5;
}

/**
 * Tenant-default PUT body: only fields the operator changed versus the
 * stored tenant default. Fields never set stay inherited from system.
 */
export function tenantDefaultsPayload(stored, edited) {
  const out = {};
  for (const f of DOC_SETTING_FIELDS) {
    if (!(f in edited)) continue;
    const before = stored ? stored[f] : undefined;
    if (JSON.stringify(before ?? null) !== JSON.stringify(edited[f] ?? null)) out[f] = edited[f];
  }
  return out;
}

/**
 * Catalog override PUT body: only the fields explicitly marked as
 * overridden with a value that differs from the inherited one. An
 * inherited field is never sent, so one changed field never replaces the
 * whole inherited configuration.
 */
export function catalogOverridePayload(effectiveSettings, overrideFlags, edited) {
  const out = {};
  for (const f of DOC_SETTING_FIELDS) {
    if (!overrideFlags[f]) continue;
    const value = edited[f];
    if (value === undefined) continue;
    out[f] = value;
  }
  return out;
}

/**
 * Client-side effective preview for a catalog scope: an overridden field
 * takes the edited value (source catalog); a field that is not overridden
 * falls back to the tenant-scope effective value (source tenant or system).
 */
export function previewEffective(tenantEffective, overrideFlags, edited) {
  const base = tenantEffective?.settings || {};
  const baseSources = tenantEffective?.sources || {};
  const settings = {};
  const sources = {};
  for (const f of DOC_SETTING_FIELDS) {
    if (overrideFlags[f] && edited[f] !== undefined) {
      settings[f] = edited[f]; sources[f] = "catalog";
    } else {
      settings[f] = base[f]; sources[f] = baseSources[f] || "system";
    }
  }
  return { settings, sources };
}

// ------------------------------------------------------------- layout labels
export const SECTION_LABELS = {
  logo: "Logo", business_name: "Business name", dba_name: "Doing-business-as name", address: "Address",
  phone: "Phone", website: "Website", tax_id: "Tax ID", order_number: "Order number", order_channel: "Order channel",
  order_datetime: "Date and time", table_number: "Table number", customer_name: "Customer name",
  customer_phone_masked: "Customer phone (masked)", customer_email: "Customer email (masked)",
  customer_id: "Member / customer ID", member_status: "Member status", loyalty_points: "Loyalty points",
  line_items: "Items", modifiers: "Modifiers", quantities: "Quantities", unit_prices: "Prices",
  discounts: "Discounts", coupon_code: "Coupon code", tax: "Tax", delivery_charge: "Delivery fee", tip: "Tip",
  payment_status: "Payment status", payment_method: "Payment method (safe label)", order_notes: "Order notes",
  applied_offers: "Offer details", promo_footer: "Promotional / coupon footer", policy_text: "Policy text",
  thank_you: "Thank-you text", qr_lookup: "Order lookup code", kitchen_notes: "Kitchen notes",
  item_count: "Item count", packing_checklist: "Packing checklist", reprint_label: "Reprint label",
};

export function sectionLabel(key) {
  return SECTION_LABELS[key] || key.replace(/_/g, " ");
}

export const LAYOUT_TEXT_LIMITS = { maxLineChars: 160, maxLines: 6, maxNameChars: 80 };

const UNSAFE_TEXT = /<|>|\{\{|\}\}|\{%|%\}|javascript:|<script|\$\{/i;

export function textProblem(value, { maxChars = LAYOUT_TEXT_LIMITS.maxLineChars } = {}) {
  if (value == null || value === "") return null;
  if (typeof value !== "string") return "Must be plain text.";
  if (UNSAFE_TEXT.test(value)) return "Plain text only — markup, templates and scripts are not allowed.";
  if (value.trim().length > maxChars) return `Keep it under ${maxChars} characters.`;
  return null;
}

export function linesProblem(lines) {
  if (!lines) return null;
  if (lines.length > LAYOUT_TEXT_LIMITS.maxLines) return `At most ${LAYOUT_TEXT_LIMITS.maxLines} lines.`;
  for (const l of lines) {
    const p = textProblem(l);
    if (p) return p;
  }
  return null;
}

export function splitLines(text) {
  return (text || "").split("\n").map((l) => l.trim()).filter(Boolean);
}

/** Layout POST body restricted to the controlled fields the backend accepts. */
export function layoutPayload(docType, allowedSections, form) {
  const sections = {};
  for (const key of Object.keys(allowedSections || {})) {
    if (key in (form.sections || {})) sections[key] = Boolean(form.sections[key]);
  }
  return {
    name: (form.name || "").trim() || "Custom layout",
    sections,
    header_lines: splitLines(form.header_text),
    footer_lines: splitLines(form.footer_text),
    policy_text: (form.policy_text || "").trim(),
    promo_footer: (form.promo_footer || "").trim(),
    thank_you_text: (form.thank_you_text || "").trim(),
  };
}

export function validateLayoutForm(form) {
  const errors = {};
  const nameP = textProblem(form.name, { maxChars: LAYOUT_TEXT_LIMITS.maxNameChars });
  if (nameP) errors.name = nameP;
  const hp = linesProblem(splitLines(form.header_text)); if (hp) errors.header_text = hp;
  const fp = linesProblem(splitLines(form.footer_text)); if (fp) errors.footer_text = fp;
  for (const f of ["policy_text", "promo_footer", "thank_you_text"]) {
    const p = textProblem(form[f]); if (p) errors[f] = p;
  }
  return { valid: Object.keys(errors).length === 0, errors };
}

// ----------------------------------------------------------- operator errors
const CODE_MESSAGES = {
  automatic_packing_slip_legacy_rule_conflict: "Disable the existing packing-slip rule under Rules & Triggers before enabling Automatic Packing Slips.",
  automatic_packing_slip_rule_managed: "Managed by Automatic Packing Slips. Turn it on or off from the Printing Overview.",
  packing_slip_rules_managed: "Packing-slip rules are managed by Automatic Packing Slips on the Printing Overview. An existing packing-slip rule can only be disabled or deleted here.",
  automatic_packing_slip_managed_rule_duplicate: "Automatic Packing Slips found duplicate managed rules. Nothing was changed; contact support.",
  automatic_packing_slip_finish_setup_required: "Automatic packing slips are active, but the rules need to be secured as managed rules. Use Finish setup on the Overview.",
  automatic_packing_slip_rules_changed: "The packing-slip rules changed while saving. Nothing was changed; try again.",
  no_agent: "No print agent is registered. Issue an agent token and set it up on the tablet first.",
  agent_offline: "The print agent has not sent a heartbeat recently. Check that the tablet is on and the Print Agent service is running.",
  no_printer: "No printer is assigned for this document. Add a printer and assign it a role or set it as the default.",
  printer_not_found: "That printer no longer exists.",
  printer_disabled: "This printer is disabled. Enable it before printing.",
  printer_unreachable: "The agent could not reach the printer on the LAN. Check the printer's power, cable and address.",
  unsupported_profile: "That printer profile is not certified in this system.",
  invalid_host: "Enter a private LAN address (10.x, 172.16–31.x or 192.168.x) or a local hostname.",
  public_address_forbidden: "Public Internet addresses are not allowed. The printer must be on the business's local network.",
  hostname_not_local: "Use a local hostname such as kitchen-printer or kitchen.local.",
  loopback_forbidden: "Loopback is not a printer address.",
  invalid_port: "Port must be between 1 and 65535.",
  invalid_columns: "Columns must be 16–96.",
  invalid_paper_width: "Paper width must be 40–120 mm.",
  invalid_connect_timeout: "Connect timeout must be 100–60000 ms.",
  invalid_write_timeout: "Write timeout must be 100–60000 ms.",
  agent_not_found: "That print agent does not exist or was revoked.",
  agent_revoked: "That agent token was revoked. Issue a new token.",
  rule_exists: "A rule for this document and trigger already exists in this scope. Edit the existing rule instead.",
  invalid_copy_count: "Copies must be between 1 and 5.",
  copy_count_out_of_range: "Copies must be between 1 and 5.",
  unsafe_text: "Plain text only — markup, templates and scripts are not allowed.",
  text_too_long: "That text is too long.",
  unknown_section: "That layout section is not supported for this document.",
  forbidden_section: "That section can never be printed.",
  reprint_blocked_queued: REPRINT_BLOCKED.queued,
  reprint_blocked_in_progress: "The original job is currently being processed. Wait for a final outcome.",
  reprint_blocked_retry_pending: REPRINT_BLOCKED.failed_retryable,
  reprint_blocked_cancelled: REPRINT_BLOCKED.cancelled,
  reprint_blocked_unknown_state: "This job is in an unrecognised state and cannot be reprinted safely.",
  duplicate_risk_unacknowledged: "The printer may already have produced this document. Tick the acknowledgement to reprint anyway.",
  job_not_found: "That print job no longer exists.",
  original_job_not_found: "The original job could not be found.",
  invalid_state_for_cancel: "Only queued jobs can be cancelled.",
  printing_admin_required: "Only a business administrator can change printing settings.",
  printing_tenant_missing: "Your account is not linked to a business.",
  unsupported_doc_type: "That document type is not configurable.",
  auto_print_since_in_past: "The activation time is too far in the past. Automatic receipts never apply to orders paid before they were enabled.",
  invalid_auto_print_since: "The activation time was not understood. Nothing was changed.",
  configuration_conflict: "That change conflicts with the current configuration. Reload and try again.",
  backend_unavailable: "The Rudras-AI service is unavailable right now. Nothing was changed; try again shortly.",
  authentication_failed: "Your session has expired. Sign in again.",
};

/** Maps an axios error (or a backend code string) to an operator-safe message. Never echoes bodies. */
export function operatorMessage(errOrCode, fallback = "Something went wrong. Nothing was changed.") {
  if (!errOrCode) return fallback;
  if (typeof errOrCode === "string") return CODE_MESSAGES[errOrCode] || fallback;
  const status = errOrCode?.response?.status;
  const detail = errOrCode?.response?.data?.detail;
  if (!errOrCode.response) return CODE_MESSAGES.backend_unavailable;
  if (status === 401) return CODE_MESSAGES.authentication_failed;
  if (status === 403) return CODE_MESSAGES.printing_admin_required;
  if (status >= 500) return CODE_MESSAGES.backend_unavailable;
  let code = null;
  let message = null;
  if (detail && typeof detail === "object" && !Array.isArray(detail)) {
    code = detail.code || null; message = detail.message || null;
  } else if (typeof detail === "string") {
    code = detail.split(":")[0].trim();
  }
  if (code && CODE_MESSAGES[code]) return CODE_MESSAGES[code];
  if (message && typeof message === "string" && message.length <= 200 && !/<|token|bearer|secret/i.test(message)) return message;
  if (status === 409) return CODE_MESSAGES.configuration_conflict;
  return fallback;
}

export function errorCode(err) {
  const detail = err?.response?.data?.detail;
  if (detail && typeof detail === "object" && !Array.isArray(detail)) return detail.code || null;
  if (typeof detail === "string") return detail.split(":")[0].trim();
  return null;
}

// ------------------------------------------------------------------ overview
export const AGENT_ONLINE_WINDOW_MS = 2 * 60 * 1000;

export function agentIsOnline(agent, nowMs = Date.now()) {
  if (!agent || agent.revoked_at || !agent.last_heartbeat_at) return false;
  const t = Date.parse(agent.last_heartbeat_at);
  return Number.isFinite(t) && nowMs - t <= AGENT_ONLINE_WINDOW_MS;
}

export function printerReachability(p) {
  if (!p || p.enabled === false) return "disabled";
  if (p.last_reachability_status === "reachable") return "reachable";
  if (p.last_reachability_status === "unreachable") return "unreachable";
  return "unknown";
}

/**
 * The error text worth showing beside the printer's current status.
 * Newest event wins: an older failure is hidden once a newer success — a
 * reachable agent test or a transmitted job — is on record, so "Reachable
 * (agent report)" never sits next to a stale "connection refused". An
 * undated error with a dated success is treated as stale; an error with
 * no success on record is always shown.
 */
export function printerVisibleError(p) {
  if (!p || !p.last_error) return null;
  const successes = [];
  if (p.last_reachability_status === "reachable") successes.push(Date.parse(p.last_reachability_at || ""));
  if (p.last_transmitted_at) successes.push(Date.parse(p.last_transmitted_at));
  const newestSuccess = successes.filter(Number.isFinite).sort((a, b) => b - a)[0];
  if (newestSuccess === undefined) return p.last_error;
  const errAt = Date.parse(p.last_error_at || "");
  if (!Number.isFinite(errAt)) return null;
  return errAt > newestSuccess ? p.last_error : null;
}

/**
 * Warnings for document types that are enabled but cannot be routed:
 * no pinned printer and no enabled printer with the document's role (or
 * the general role). Disabled document types never warn — a tenant with
 * only a receipt printer and kitchen tickets switched off sees nothing.
 * Each warning names its document type so the operator can see that it
 * does not affect the others.
 */
export function unroutableDocumentWarnings(effective, printers = []) {
  const out = [];
  if (!effective) return out;
  const livePrinters = printers.filter((p) => !p.deleted_at && p.enabled !== false);
  for (const [dt, v] of Object.entries(effective)) {
    const s = v?.settings || {};
    if (!s.enabled) continue;
    if (s.printer_id) continue;
    const role = s.printer_role || "general";
    if (livePrinters.some((p) => p.role === role || p.role === "general")) continue;
    const label = DOC_TYPE_LABELS[dt] || dt;
    out.push({
      code: "no_printer_for_role",
      docType: dt,
      text: `${label}: no enabled printer has the "${ROLE_LABELS[role] || role}" role. ` +
        `Only ${label.toLowerCase()} printing is affected — other document types still print. ` +
        `If you do not print ${label.toLowerCase()}s, turn that document type off under Document defaults.`,
    });
  }
  return out;
}

export function summarizeOverview({ agents = [], printers = [], jobs = [], effective = null, nowMs = Date.now() } = {}) {
  const active = agents.filter((a) => !a.revoked_at);
  const online = active.filter((a) => agentIsOnline(a, nowMs)).length;
  const livePrinters = printers.filter((p) => !p.deleted_at);
  const reach = { reachable: 0, unreachable: 0, unknown: 0, disabled: 0 };
  livePrinters.forEach((p) => { reach[printerReachability(p)] += 1; });
  const byState = {};
  jobs.forEach((j) => { byState[j.state] = (byState[j.state] || 0) + 1; });
  const transmittedJobs = jobs.filter((j) => j.state === "transmitted" || j.state === "completed");
  const lastTransmission = transmittedJobs
    .map((j) => j.transmitted_at || j.completed_at || j.print_finish_at)
    .filter(Boolean).sort().slice(-1)[0] || null;
  const recentErrors = jobs
    .filter((j) => ["failed_terminal", "failed", "ambiguous", "failed_retryable"].includes(j.state))
    .slice(0, 5)
    .map((j) => ({ id: j.id, state: j.state, code: j.error_code || null, reason: j.failure_reason || null, at: j.print_finish_at || j.created_at }));
  const warnings = [];
  if (active.length === 0) warnings.push({ code: "no_agent", text: CODE_MESSAGES.no_agent });
  else if (online === 0) warnings.push({ code: "agent_offline", text: CODE_MESSAGES.agent_offline });
  if (livePrinters.filter((p) => p.enabled !== false).length === 0) warnings.push({ code: "no_printer", text: CODE_MESSAGES.no_printer });
  if (reach.unreachable > 0) warnings.push({ code: "printer_unreachable", text: `${reach.unreachable} printer(s) reported unreachable by the agent.` });
  const enabledDocs = effective ? Object.entries(effective).filter(([, v]) => v?.settings?.enabled).map(([k]) => k) : [];
  if (effective && enabledDocs.length === 0) warnings.push({ code: "printing_disabled", text: "Printing is disabled for every document type." });
  warnings.push(...unroutableDocumentWarnings(effective, livePrinters));
  if ((byState.ambiguous || 0) > 0) warnings.push({ code: "ambiguous", text: `${byState.ambiguous} job(s) have an unknown outcome and need a decision.` });
  return {
    printingEnabled: enabledDocs.length > 0,
    enabledDocs,
    agents: { total: active.length, online, offline: active.length - online },
    printers: { total: livePrinters.length, ...reach },
    jobs: {
      queued: byState.queued || 0,
      transmitting: (byState.transmitting || 0) + (byState.claimed || 0) + (byState.rendering || 0) + (byState.submitted_to_spooler || 0),
      transmitted: (byState.transmitted || 0) + (byState.completed || 0),
      retryable: byState.failed_retryable || 0,
      terminal: (byState.failed_terminal || 0) + (byState.failed || 0),
      ambiguous: byState.ambiguous || 0,
      cancelled: byState.cancelled || 0,
    },
    lastTransmission,
    recentErrors,
    warnings,
  };
}

// ------------------------------------------------------------------ formatting
/**
 * Client-side check for the receipt time-zone setting. Empty is allowed
 * (the backend then prints labelled UTC). Anything else must be an IANA
 * name the browser's own zone database knows; the backend validates
 * again against its zone database.
 */
export function timezoneProblem(value) {
  const v = String(value || "").trim();
  if (!v) return null;
  if (v.length > 64 || /[\s<>{}"']/.test(v)) return "Use an IANA zone name such as America/Chicago.";
  try {
    new Intl.DateTimeFormat("en-US", { timeZone: v }).format(new Date(0));
    return null;
  } catch {
    return `Unknown time zone "${v}". Use an IANA zone name such as America/Chicago.`;
  }
}

/** "12:16 PM CDT"-style sample of the current time in a zone; null when the zone is unusable. */
export function timezoneSample(value, at = new Date()) {
  const v = String(value || "").trim() || "UTC";
  try {
    return new Intl.DateTimeFormat("en-US", { timeZone: v, hour: "2-digit", minute: "2-digit", timeZoneName: "short" }).format(at);
  } catch {
    return null;
  }
}

export function formatWhen(iso) {
  if (!iso) return "never";
  const t = Date.parse(iso);
  if (!Number.isFinite(t)) return String(iso);
  return new Date(t).toLocaleString();
}

export function shortId(id) {
  return id ? `${String(id).slice(0, 8)}…` : "—";
}

/** Job-list filter, all client-side; `dateFrom`/`dateTo` are YYYY-MM-DD. */
export function filterJobs(jobs, f = {}) {
  return (jobs || []).filter((j) => {
    if (f.ambiguousOnly && j.state !== "ambiguous") return false;
    if (f.state && j.state !== f.state) return false;
    if (f.docType && j.doc_type !== f.docType) return false;
    if (f.printerId && j.printer_id !== f.printerId) return false;
    if (f.order) {
      const needle = f.order.trim().toLowerCase();
      const hay = `${j.order_id || ""} ${j.snapshot?.order_code || ""} ${j.id || ""}`.toLowerCase();
      if (!hay.includes(needle)) return false;
    }
    if (f.dateFrom && (j.created_at || "") < f.dateFrom) return false;
    if (f.dateTo && (j.created_at || "").slice(0, 10) > f.dateTo) return false;
    return true;
  });
}

/** Audit rows → timeline steps the operator reads top to bottom. */
export function auditTimeline(audit) {
  return (audit || []).map((row) => ({
    id: row.id,
    at: row.at,
    action: row.action,
    from: row.from_state || null,
    to: row.to_state || row.state || null,
    actor: row.actor_user_id || (row.extra && row.extra.agent_id ? `agent ${row.extra.agent_id}` : null),
    attempt: row.attempt_count ?? null,
    detail: row.extra || null,
  }));
}
